import { HttpClient } from './httpClient';
import { API_ENDPOINTS } from '@/config/api';
import { locationService, State as LocationState } from './locationService';
import {
  Vehicle,
  VehicleDetail,
  VehicleDetailDto,
  VehicleGridDto,
  VehicleGridResponseDto,
  VehicleLookupTablesDto,
  SaveVehicleRequestDto,
  BulkUpdateVehicleStatusRequestDto,
  PaginatedVehicles,
  DriverDto,
  DriverGeoDto,
} from '@/types/manage';

/**
 * Lazy loader for the full US-state catalog (used by Vehicle License Plate
 * dropdowns — scope-independent unlike Assigned Areas).
 *
 * Returns immediately as a no-op when `current` already has entries; only
 * fetches the first time `setStates` will receive data. Parent screens own
 * the `current` array, so the cache is scoped to their lifetime — leaving
 * the screen drops the array and a re-entry re-fetches.
 *
 * Shared by `Manage.tsx` (Fleet) and `ComplianceRequirementsView.tsx`
 * (Rental/Personal) so the check + fetch + error-swallow lives in one place.
 */
export function ensureAllStatesLoaded(
  current: LocationState[],
  setStates: (states: LocationState[]) => void
): void {
  if (current.length > 0) return;
  locationService.fetchStates(false).then(setStates).catch(() => {
    /* dropdown stays empty; reopening the dialog will retry */
  });
}


// ============================================================================
// MAPPER
// ============================================================================

function mapVehicleGridDto(dto: VehicleGridDto): Vehicle {
  return {
    id: dto.id,
    name: dto.name,
    modelLabel: `${dto.year} ${dto.make} ${dto.model}`,
    typeLabel: dto.color,
    make: dto.make,
    model: dto.model,
    year: dto.year,
    color: dto.color,
    isActive: dto.statusName === 'Active',
    hasWarning: dto.statusName !== 'Active',
    complianceStatus: dto.complianceStatusName,
    wheelchairAccessible: dto.wheelChairTypeId !== null,
    ...(dto.wheelChairTypeName ? { wheelchairAccessibilityType: dto.wheelChairTypeName } : {}),
    ...(dto.wheelChairCapacity === null ? {} : { wheelchairCapacity: dto.wheelChairCapacity ?? undefined }),
    seatingCapacity: dto.seatingCapacity,
    driverNames: (dto.assignedDrivers ?? []).map((d) => d.driverName),
    driverIds: (dto.assignedDrivers ?? []).map((d) => d.driverId),
    assignedStates: (dto.assignedStates ?? []).map((s) => s.stateName),
    assignedDistricts: (dto.assignedDistricts ?? []).map((d) => d.districtName),
    assignedDispatchAreas: [],
  };
}

function mapVehicleDetailDto(dto: VehicleDetailDto, driversList: DriverDto[]): VehicleDetail {
  return {
    id: dto.id,
    name: dto.name,
    modelLabel: `${dto.year} ${dto.make} ${dto.model}`,
    make: dto.make,
    model: dto.model,
    year: dto.year,
    typeName: dto.typeName,
    colorName: dto.colorName,
    vin: dto.vin ?? null,
    licensePlate: dto.licensePlate,
    licensePlateStateId: dto.licensePlateStateId,
    registrationExpiration: dto.registrationExpiration ?? null,
    seatingCapacity: dto.seatingCapacity,
    wheelchairAccessible: dto.wheelChairTypeId !== null,
    wheelchairType: dto.wheelChairTypeName ?? null,
    wheelchairCapacity: dto.wheelChairCapacity ?? null,
    isActive: dto.statusName === 'Active',
    statusName: dto.statusName,
    complianceStatus: dto.complianceStatusName,
    complianceStatusId: dto.complianceStatusId,
    drivers: (dto.assignedDrivers ?? []).map((d) => {
      const match = driversList.find((dr) => dr.id === d.driverId);
      return match ?? { id: d.driverId, name: d.driverName, email: '', mobileNumber: '' };
    }),
    assignedStates: (dto.assignedStates ?? []).map((s) => s.stateName),
    assignedStateIds: (dto.assignedStates ?? []).map((s) => s.stateId),
    assignedDispatchAreas: (dto.assignedDispatchAreas ?? []).map((d) => d.dispatchAreaName),
    assignedDispatchAreaIds: (dto.assignedDispatchAreas ?? []).map((d) => d.dispatchAreaId),
    assignedDistricts: (dto.assignedDistricts ?? []).map((d) => d.districtName),
    assignedDistrictIds: (dto.assignedDistricts ?? []).map((d) => d.districtId),
    changeLogs: dto.changeLogs ?? [],
    kindId: dto.kindId,
    isPrimary: dto.isPrimary,
    serviceProviderId: dto.serviceProviderId,
    otherVehicleType: dto.otherVehicleType ?? null,
    otherColor: dto.otherColor ?? null,
  };
}

// ============================================================================
// SERVICE
// ============================================================================

class ManageService {
  private async fetchWithAuth<T>(url: string, options: RequestInit = {}) {
    return HttpClient.fetchWithAuth<T>(url, options);
  }

  /**
   * GET /vehicles/fleet — paginated fleet vehicle list.
   * Sort and filter params are applied server-side across the full result set
   * (see GetFleetVehiclesQueryHandler on the backend) so pagination is correct.
   */
  async fetchVehicles(params?: {
    pageNumber?: number;
    pageSize?: number;
    serviceProviderId?: string;
    inactiveOnly?: boolean;
    searchString?: string;
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
    driverIds?: string[];
    wheelchairTypes?: string[];
    complianceStatuses?: string[];
    assignedStateNames?: string[];
    maxSeatingCapacity?: number;
  }): Promise<PaginatedVehicles> {
    const query = new URLSearchParams();
    if (params?.pageNumber) query.set('pageNumber', String(params.pageNumber));
    if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    if (params?.serviceProviderId) query.set('serviceProviderId', params.serviceProviderId);
    if (params?.inactiveOnly) query.set('inactiveOnly', 'true');
    if (params?.searchString) query.set('searchString', params.searchString);
    if (params?.sortField) query.set('sortField', params.sortField);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    if (params?.maxSeatingCapacity !== undefined) query.set('maxSeatingCapacity', String(params.maxSeatingCapacity));
    // ASP.NET Core model-binds string[]/Guid[] from repeated ?key=value pairs.
    params?.driverIds?.forEach((id) => query.append('driverIds', id));
    params?.wheelchairTypes?.forEach((t) => query.append('wheelchairTypes', t));
    params?.complianceStatuses?.forEach((c) => query.append('complianceStatuses', c));
    params?.assignedStateNames?.forEach((s) => query.append('assignedStateNames', s));

    const qs = query.toString();
    const queryString = qs ? `?${qs}` : '';
    const url = `${API_ENDPOINTS.vehicles.fleet()}${queryString}`;
    const response = await this.fetchWithAuth<VehicleGridResponseDto>(url);
    const data = (response as any).data ?? response;
    const dto = data as VehicleGridResponseDto;

    const resolvedPageSize = dto.pageSize ?? params?.pageSize ?? 25;
    return {
      vehicles: (dto.vehicles ?? []).map(mapVehicleGridDto),
      total: dto.totalCount ?? 0,
      pageNumber: dto.pageNumber ?? params?.pageNumber ?? 1,
      pageSize: resolvedPageSize,
      totalPages:
        dto.totalCount && resolvedPageSize
          ? Math.ceil(dto.totalCount / resolvedPageSize)
          : 1,
    };
  }

  /**
   * GET /lookuptables — all vehicle lookup tables (types, colors, statuses, etc.)
   */
  async fetchLookupTables(): Promise<VehicleLookupTablesDto> {
    const response = await this.fetchWithAuth<VehicleLookupTablesDto>(
      API_ENDPOINTS.vehicles.lookupTables()
    );
    return ((response as any).data ?? response) as VehicleLookupTablesDto;
  }

  /**
   * GET /users/drivers — drivers for the current service provider.
   * Pass spId to scope to a specific SP (internal-user flow browsing an SP);
   * omit for SpStaff, whose JWT service_provider_id claim is used server-side.
   */
  async fetchDrivers(spId?: string): Promise<DriverDto[]> {
    const response = await this.fetchWithAuth<DriverDto[]>(API_ENDPOINTS.users.drivers(spId));
    return ((response as any).data ?? response) as DriverDto[];
  }

  /**
   * GET /users/drivers/{id}/geo — driver's authorized state / district /
   * dispatch-area IDs (COMP-1444 / COMP-1545). Used by the Personal Vehicle
   * dialog to render the "Inherited from driver" summary.
   */
  async fetchDriverGeo(driverId: string): Promise<DriverGeoDto> {
    const response = await this.fetchWithAuth<DriverGeoDto>(
      API_ENDPOINTS.users.driverGeo(driverId)
    );
    return ((response as any).data ?? response) as DriverGeoDto;
  }

  /**
   * GET /vehicles/{id} — returns full vehicle detail including changelog.
   * Enriches assigned drivers with email/phone from the pre-fetched drivers list.
   */
  async fetchVehicleById(id: string, driversList: DriverDto[]): Promise<VehicleDetail> {
    const response = await this.fetchWithAuth<VehicleDetailDto>(API_ENDPOINTS.vehicles.getById(id));
    const dto = ((response as any).data ?? response) as VehicleDetailDto;
    return mapVehicleDetailDto(dto, driversList);
  }

  /**
   * POST /vehicles
   */
  async createVehicle(payload: SaveVehicleRequestDto): Promise<Vehicle> {
    const response = await this.fetchWithAuth<VehicleGridDto>(API_ENDPOINTS.vehicles.create(), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const dto = ((response as any).data ?? response) as VehicleGridDto;
    return mapVehicleGridDto(dto);
  }

  /**
   * PUT /vehicles/{id}
   */
  async updateVehicle(id: string, payload: SaveVehicleRequestDto): Promise<Vehicle> {
    const response = await this.fetchWithAuth<VehicleGridDto>(API_ENDPOINTS.vehicles.update(id), {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    const dto = ((response as any).data ?? response) as VehicleGridDto;
    return mapVehicleGridDto(dto);
  }

  /**
   * DELETE /vehicles/{id}
   */
  async deleteVehicle(id: string): Promise<void> {
    await this.fetchWithAuth<void>(API_ENDPOINTS.vehicles.delete(id), { method: 'DELETE' });
  }

  /**
   * Deactivate one or more vehicles via PATCH /vehicles/status/bulk.
   * statusId must be resolved from lookupTables.vehicleStatuses by the caller.
   */
  async deactivateVehicles(vehicleIds: string[], statusId: string): Promise<void> {
    const payload: BulkUpdateVehicleStatusRequestDto = {
      vehicleIds,
      statusId,
    };
    await this.fetchWithAuth<void>(API_ENDPOINTS.vehicles.bulkUpdateStatus(), {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }
}

export const manageService = new ManageService();
export default manageService;